import { clsx } from "clsx";
import type { KeyboardEvent, MouseEvent } from "react";
import { useCallback, useMemo, useRef } from "react";
import type { IsoDate, Weekday } from "../../utils/date";
import {
  addDays,
  addMonths,
  addYears,
  firstOfMonth,
  formatIsoDate,
  isoDate,
  monthGrid,
  monthOf,
  placeInWeek,
  weekdays,
} from "../../utils/date";
import { useCalendarContext } from "./context";
import { useFocusRequest } from "./useFocusRequest";

const DAYS_IN_WEEK = 7;

/** 2024-01-01 was a Monday: day `weekday` of that week names the weekday. */
const weekdaySample = (weekday: number): IsoDate => isoDate(2024, 1, weekday);

/** Where a key moves the cursor (APG date picker dialog); undefined for other keys. */
const target = (
  e: KeyboardEvent,
  day: IsoDate,
  firstDay: Weekday,
): IsoDate | null | undefined => {
  const step = e.shiftKey ? addYears : addMonths;
  switch (e.key) {
    case "ArrowLeft":
      return addDays(day, -1);
    case "ArrowRight":
      return addDays(day, 1);
    case "ArrowUp":
      return addDays(day, -DAYS_IN_WEEK);
    case "ArrowDown":
      return addDays(day, DAYS_IN_WEEK);
    case "Home":
      return addDays(day, -placeInWeek(day, firstDay));
    case "End":
      return addDays(day, DAYS_IN_WEEK - 1 - placeInWeek(day, firstDay));
    case "PageUp":
      return step(day, -1);
    case "PageDown":
      return step(day, 1);
  }
  return undefined;
};

export const Days = () => {
  const {
    value,
    today,
    first,
    last,
    firstDay,
    locale,
    markers,
    cursor,
    moveTo,
    pick,
  } = useCalendarContext();
  const { formats } = locale;
  const gridRef = useRef<HTMLDivElement>(null);
  useFocusRequest(gridRef);

  const shown = monthOf(cursor);

  const weeks = useMemo(() => {
    const slots = monthGrid({ year: shown.year, month: shown.month }, firstDay);
    const result: (IsoDate | null)[][] = [];
    for (let i = 0; i < slots.length; i += DAYS_IN_WEEK) {
      result.push(slots.slice(i, i + DAYS_IN_WEEK));
    }
    return result;
  }, [shown.year, shown.month, firstDay]);

  const heads = useMemo(
    () =>
      weekdays(firstDay).map((weekday) => {
        const sample = weekdaySample(weekday);
        // Two letters, as the kit has always shown them: "Su", "Mo".
        const short = formatIsoDate(sample, formats.weekdayShort)
          .replace(/\.$/, "")
          .slice(0, 2);
        return {
          weekday,
          short,
          long: formatIsoDate(sample, formats.weekdayLong),
        };
      }),
    [firstDay, formats],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const day = (e.target as HTMLElement).dataset.date;
      if (!day) return;
      const next = target(e, day, firstDay);
      if (next === undefined) return;
      e.preventDefault();
      moveTo(next, true);
    },
    [firstDay, moveTo],
  );

  const onClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const day = e.currentTarget.dataset.date;
      if (!day) return;
      moveTo(day, false);
      pick(day);
    },
    [moveTo, pick],
  );

  const renderDay = (day: IsoDate | null, index: number) => {
    if (day === null) {
      return (
        <div
          key={`empty-${index}`}
          role="gridcell"
          className="nuco-calendar__day nuco-calendar__day--empty"
        />
      );
    }

    const disabled = day < first || day > last;
    const isToday = day === today;
    const selected = day === value;
    const markerColor = markers[day];

    return (
      <button
        key={day}
        type="button"
        role="gridcell"
        data-date={day}
        className={clsx(
          "nuco-calendar__dateButton nuco-calendar__day",
          isToday && "nuco-calendar__dateButton--today",
          selected && "nuco-calendar__dateButton--selected",
          disabled && "nuco-calendar__dateButton--disabled",
        )}
        tabIndex={day === cursor ? 0 : -1}
        aria-label={formatIsoDate(day, formats.full)}
        aria-selected={selected}
        aria-current={isToday ? "date" : undefined}
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : onClick}
      >
        {Number(day.slice(8, 10))}
        {markerColor && (
          <span
            className={`nuco-calendar__dateButton-marker nuco-color-${markerColor}`}
            aria-hidden="true"
          />
        )}
      </button>
    );
  };

  return (
    <div
      ref={gridRef}
      role="grid"
      aria-label={formatIsoDate(firstOfMonth(shown), formats.monthYear)}
      className="nuco-calendar__days-wrapper nuco-text nuco-text--md nuco-text--medium"
      onKeyDown={onKeyDown}
    >
      <div role="row" className="nuco-calendar__weekDays">
        {heads.map((head) => (
          <div
            key={head.weekday}
            role="columnheader"
            aria-label={head.long}
            className="nuco-calendar__weekDay"
          >
            {head.short}
          </div>
        ))}
      </div>
      {weeks.map((week, row) => (
        <div key={row} role="row" className="nuco-calendar__week">
          {week.map((day, column) =>
            renderDay(day, row * DAYS_IN_WEEK + column),
          )}
        </div>
      ))}
    </div>
  );
};
